import { useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ChronologyModal from './components/ChronologyModal';
import { InconsistenciesSection } from './components/Inconsistencies/InconsistenciesSection';
import InconsistencyFullView from './components/Inconsistencies/InconsistencyFullView';
import { KeyDocumentsSection } from './components/KeyDocumentsSection';
import { KeyEntitiesSection } from './components/KeyEntitiesSection';
import { KeyFactsSection } from './components/KeyFactsSection';
import { AskKimSection } from './components/Kim/AskKimSection';
import { CaseSummarySection } from './components/Summary/CaseSummarySection';
import FullSummaryView from './components/Summary/FullSummaryView';
import { SummaryLoader } from './components/SummaryLoader';
import ThemeFullView from './components/Themes/ThemeFullView';
import { ThemesSection } from './components/Themes/ThemesSection';
import { PageName } from '@/analytics/events';
import { trackPageView } from '@/analytics/Mixpanel';
import { useGetCase } from '@/api/queries/useGetCase';
import useGetCaseOverview from '@/api/queries/useGetCaseOverview';
import { useGetInconsistencies } from '@/api/queries/useGetInconsistencies';
import { useGetKeyFacts } from '@/api/queries/useGetKeyFacts';
import useGetSummary from '@/api/queries/useGetSummary';
import Modal from '@/components/molecules/Modals/CustomModal';
import { KimMode, Run, Summary } from '@/types';

const CaseSummary = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const caseId = params.matterId ?? searchParams.get('caseId');

  // State
  const [chronologyExplorerOpen, setChronologyExplorerOpen] = useState(false);
  const [themes, setThemes] = useState<Summary[]>([]);
  const [summaryText, setSummaryText] = useState<string>('');
  const [expandedSection, setExpandedSection] = useState<{
    type: 'summary' | 'theme' | 'inconsistency';
    data: any;
  } | null>(null);
  const [hasTransitioned, setHasTransitioned] = useState(false);

  // Data
  const { data: summaries, isLoading: summaryIsLoading } = useGetSummary(caseId);
  const { data: overview, isLoading: overviewIsLoading } = useGetCaseOverview(caseId);
  const { data: keyFacts, isLoading: keyFactsIsLoading } = useGetKeyFacts(caseId);
  const { data: caseObject } = useGetCase(caseId);

  // Fetch top 3 inconsistencies
  const { data: inconsistenciesData, isLoading: inconsistenciesLoading } = useGetInconsistencies({
    caseId,
    page: 1,
    pageSize: 3,
    filters: {},
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (!summaries || !summaries.content || !summaries.content.length) return;

    // Set the summary text to the first summary content
    setSummaryText(summaries.content[0].content);

    // Set the themes to the rest of the summaries
    setThemes(summaries.content.slice(1));
  }, [summaries]);

  useEffect(() => {
    trackPageView(PageName.CaseSummary);
  }, []);

  // Handlers
  const handleAskQuestion = (question: string, mode: KimMode) => {
    if (!caseId || !question.trim()) return;

    // Navigate to assistant with the question in state
    navigate('/app/chronos/case-editor/assistant?' + searchParams.toString(), { state: { question, mode } });
  };

  const checkIfDeletedDocRunOngoing = (run: Run | undefined) =>
    run && run.completedAt === null && run.type === 'deleteDocuments';

  const isDeletedDocRunOngoing = checkIfDeletedDocRunOngoing(overview?.runs?.[0]);

  const handleExpandSection = (type: 'summary' | 'theme' | 'inconsistency', data: any) => {
    setHasTransitioned(true);
    setExpandedSection({ type, data });
  };

  const handleBackToSummary = () => {
    setExpandedSection(null);
  };

  return (
    <div className="flex flex-col py-4 leading-relaxed">
      {summaryIsLoading ? (
        <SummaryLoader />
      ) : (
        <AnimatePresence mode="wait">
          {expandedSection ? (
            // Full-width expanded view
            <motion.div
              key="expanded"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {expandedSection.type === 'summary' && (
                <FullSummaryView summaryText={summaryText} keyFacts={keyFacts} onBack={handleBackToSummary} />
              )}
              {expandedSection.type === 'theme' && (
                <ThemeFullView
                  theme={expandedSection.data}
                  keyFacts={keyFacts}
                  caseId={caseId ?? undefined}
                  onBack={handleBackToSummary}
                />
              )}
              {expandedSection.type === 'inconsistency' && (
                <InconsistencyFullView
                  inconsistency={expandedSection.data}
                  caseId={caseId ?? undefined}
                  onBack={handleBackToSummary}
                />
              )}
            </motion.div>
          ) : (
            // Summary grid view
            <motion.div
              key="grid"
              initial={hasTransitioned ? { opacity: 0, x: -100 } : false}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <CaseSummarySection
                summaryText={summaryText}
                isDeletedDocRunOngoing={isDeletedDocRunOngoing ?? false}
                legalIssues={caseObject?.legalIssues ?? ''}
                isLoading={summaryIsLoading}
                caseId={caseId ?? undefined}
                onExpandSummary={() => handleExpandSection('summary', { summaryText, keyFacts })}
              />

              <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-stretch">
                <AskKimSection caseObject={caseObject} onAskQuestion={handleAskQuestion} />
              </div>

              <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-8 lg:items-stretch">
                <KeyFactsSection
                  caseId={caseId}
                  keyFacts={keyFacts}
                  keyFactsIsLoading={keyFactsIsLoading}
                  overview={overview}
                  onCreateChronology={() => setChronologyExplorerOpen(true)}
                />
                <KeyDocumentsSection caseId={caseId} overview={overview} overviewIsLoading={overviewIsLoading} />
                <KeyEntitiesSection caseId={caseId} />
              </div>

              <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch">
                {/* Inconsistencies Section */}
                <InconsistenciesSection
                  caseId={caseId}
                  inconsistenciesData={inconsistenciesData}
                  inconsistenciesLoading={inconsistenciesLoading}
                  onExpandInconsistency={(inconsistency) => handleExpandSection('inconsistency', inconsistency)}
                />

                {/* Themes Section */}
                <ThemesSection
                  themes={themes}
                  keyFacts={keyFacts}
                  isLoading={summaryIsLoading}
                  caseId={caseId ?? undefined}
                  onExpandTheme={(theme) => handleExpandSection('theme', theme)}
                />
              </div>

              {/*Chronology Modal*/}
              <Modal
                handleClose={() => {
                  setChronologyExplorerOpen(false);
                }}
                isOpen={chronologyExplorerOpen}
                content={
                  <ChronologyModal
                    totalKeyFacts={overview?.keyFacts ?? 0}
                    totalRelevantFacts={overview?.relevantFacts ?? 0}
                    caseId={caseId}
                  />
                }
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default CaseSummary;
